import { Routes } from '@angular/router'
import { _deepCopy } from '@naturalcycles/js-lib'
import {
  BreakpointAction,
  ChoiceFunction,
  ConsideringPurchaseOption,
  CycleChangesBreakpointOption,
  EligibleAppleWatchBreakpointOption,
  FeelingStressedBreakpointOption,
  GoalBreakpointOption,
  NightSleepBreakpointOption,
  PurchaseNewWatchOption,
  QuizDataInput,
  SleepWithTrackerOption,
  WhichWearableBreakpointOption,
} from '@naturalcycles/shared'
import { mapOptionToPage } from '@src/app/constants/breakpoints/cycle-changes.js'
import {
  sectionCycleChangesEnd,
  sectionCycleChangesStart,
  sectionRoutine,
} from '@src/app/constants/quiz.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { SignupQuizFlow } from '@src/app/shared/typings/interfaces/quiz.js'
export enum YourBodyOption {
  empowered = 1,
  confident = 2,
  supported = 3,
  informed = 4,
  free = 5,
  inSyncWithBody = 6,
}

export enum WhySwitchToNCOption {
  hormoneFree = 1,
  guidance = 2,
  natural = 3,
  recommendation = 4,
  effectiveness = 5,
  nonInvasive = 6,
  prescriptionFree = 7,
  easyToUse = 8,
}

export enum UnderstandingCycleOption {
  educationalResources = 1,
  medicalExpertise = 2,
  trackingTools = 3,
  support = 4,
}

export enum HesitationsBreakpointOption {
  learningCurve = 1,
  lifestyleCompatibility = 2,
  effectiveness = 3,
  dataPrivacy = 4,
  skip = 5,
}

export enum ResearchBreakpointOption {
  one = 1,
  two = 2,
  three = 3,
  four = 4,
  five = 5,
}

interface Sections {
  intro: number
  goal: number
  cycle: number
  cycleChanges: number
  needs: number // New section ab351
  routine: number
  [key: string]: number
}

export const allQuizSectionsAb351: Sections = {
  intro: 0,
  goal: 1,
  cycle: 2,
  cycleChanges: 3,
  needs: 4,
  routine: 5,
}

export const commonTransformFunction = (
  flow: SignupQuizFlow,
  choice: CycleChangesBreakpointOption,
  data?: QuizDataInput | null,
): SignupQuizFlow => {
  const page = mapOptionToPage[choice]!
  const f = _deepCopy(flow)
  const pageIndex = f[allQuizSectionsAb351.cycleChanges]?.indexOf(page)
  if (pageIndex !== -1) {
    /**
     * Delete page from the section
     */
    f[allQuizSectionsAb351.cycleChanges] = f[allQuizSectionsAb351.cycleChanges]!.filter(
      (_value, index) => {
        return index !== pageIndex
      },
    )
    return f
  }

  /**
   * Add page in the section
   */
  const startIndex = sectionCycleChangesStart.length
  if (!f[allQuizSectionsAb351.cycleChanges]?.length) {
    f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  }
  f[allQuizSectionsAb351.cycleChanges]!.splice(startIndex, 0, page)

  const quizData = data
  if (quizData?.fertilityGoal === GoalBreakpointOption.plan) {
    f[allQuizSectionsAb351.needs] = [
      Page.needsIntro,
      Page.yourBody,
      Page.understandingCycle,
      Page.statementResearch,
      Page.researchInfo,
    ]
  } else {
    f[allQuizSectionsAb351.needs] = [
      Page.needsIntro,
      Page.yourBody,
      Page.understandingCycle,
      Page.switchToNc,
      Page.hesitations,
      Page.ncInfo,
      Page.statementResearch,
      Page.researchInfo,
    ]
  }
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}

export const skipTransformFunction = (
  flow: SignupQuizFlow,
  quizData?: QuizDataInput | null,
): SignupQuizFlow => {
  const f = _deepCopy(flow)
  f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  if (quizData?.fertilityGoal === GoalBreakpointOption.plan) {
    f[allQuizSectionsAb351.needs] = [
      Page.needsIntro,
      Page.yourBody,
      Page.understandingCycle,
      Page.statementResearch,
      Page.researchInfo,
    ]
  } else {
    f[allQuizSectionsAb351.needs] = [
      Page.needsIntro,
      Page.yourBody,
      Page.understandingCycle,
      Page.switchToNc,
      Page.hesitations,
      Page.ncInfo,
      Page.statementResearch,
      Page.researchInfo,
    ]
  }
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}

export const cycleChanges: BreakpointAction<CycleChangesBreakpointOption, Page, QuizDataInput> = {
  [CycleChangesBreakpointOption.emotions]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.emotions, quizDataInput)
  },
  [CycleChangesBreakpointOption.bleeding]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.bleeding, quizDataInput)
  },
  [CycleChangesBreakpointOption.skinChanges]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.skinChanges, quizDataInput)
  },
  [CycleChangesBreakpointOption.sexAndLibido]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.sexAndLibido, quizDataInput)
  },
  [CycleChangesBreakpointOption.cervicalMucus]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.cervicalMucus, quizDataInput)
  },
  [CycleChangesBreakpointOption.pain]: (flow, quizDataInput) => {
    return commonTransformFunction(flow, CycleChangesBreakpointOption.pain, quizDataInput)
  },
  [CycleChangesBreakpointOption.skip]: (flow, quizDataInput) => {
    return skipTransformFunction(flow, quizDataInput)
  },
}

export const commonHesitationsTransformFunction = (flow: SignupQuizFlow): SignupQuizFlow => {
  const f = _deepCopy(flow)
  f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  f[allQuizSectionsAb351.needs] = [
    Page.needsIntro,
    Page.yourBody,
    Page.understandingCycle,
    Page.switchToNc,
    Page.hesitations,
    Page.ncInfo,
    Page.statementResearch,
    Page.researchInfo,
  ]
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}
export const skipHesitationsTransformFunction = (flow: SignupQuizFlow): SignupQuizFlow => {
  const f = _deepCopy(flow)
  f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  f[allQuizSectionsAb351.needs] = [
    Page.needsIntro,
    Page.yourBody,
    Page.understandingCycle,
    Page.switchToNc,
    Page.hesitations,
    Page.statementResearch,
    Page.researchInfo,
  ]
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}
export const hesitations: BreakpointAction<HesitationsBreakpointOption, Page, QuizDataInput> = {
  [HesitationsBreakpointOption.learningCurve]: flow => {
    return commonHesitationsTransformFunction(flow)
  },
  [HesitationsBreakpointOption.lifestyleCompatibility]: flow => {
    return commonHesitationsTransformFunction(flow)
  },
  [HesitationsBreakpointOption.effectiveness]: flow => {
    return commonHesitationsTransformFunction(flow)
  },
  [HesitationsBreakpointOption.dataPrivacy]: flow => {
    return commonHesitationsTransformFunction(flow)
  },
  [HesitationsBreakpointOption.skip]: flow => {
    return skipHesitationsTransformFunction(flow)
  },
}

export const disagreeResearchTransformFunction = (flow: SignupQuizFlow): SignupQuizFlow => {
  const f = _deepCopy(flow)
  f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  f[allQuizSectionsAb351.needs] = [
    Page.needsIntro,
    Page.yourBody,
    Page.understandingCycle,
    Page.switchToNc,
    Page.hesitations,
    Page.statementResearch,
  ]
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}

export const agreeResearchTransformFunction = (flow: SignupQuizFlow): SignupQuizFlow => {
  const f = _deepCopy(flow)
  f[allQuizSectionsAb351.cycleChanges] = [...sectionCycleChangesStart, ...sectionCycleChangesEnd]
  f[allQuizSectionsAb351.needs] = [
    Page.needsIntro,
    Page.yourBody,
    Page.understandingCycle,
    Page.switchToNc,
    Page.hesitations,
    Page.statementResearch,
    Page.researchInfo,
  ]
  f[allQuizSectionsAb351.routine] = sectionRoutine
  return f
}

export const statementResearch: BreakpointAction<ResearchBreakpointOption, Page, QuizDataInput> = {
  [ResearchBreakpointOption.one]: flow => {
    return disagreeResearchTransformFunction(flow)
  },
  [ResearchBreakpointOption.two]: flow => {
    return disagreeResearchTransformFunction(flow)
  },
  [ResearchBreakpointOption.three]: flow => {
    return agreeResearchTransformFunction(flow)
  },
  [ResearchBreakpointOption.four]: flow => {
    return agreeResearchTransformFunction(flow)
  },
  [ResearchBreakpointOption.five]: flow => {
    return agreeResearchTransformFunction(flow)
  },
}

const commonTransformFunctionWhichWearable: ChoiceFunction<Page, QuizDataInput> = flow => {
  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    Page.nightSleep,
    Page.sleepWearable,
    Page.resultOverview,
  ]
  return flow
}

export const whichWearable: BreakpointAction<WhichWearableBreakpointOption, Page, QuizDataInput> = {
  [WhichWearableBreakpointOption.appleWatch]: flow => {
    flow[allQuizSectionsAb351.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.eligibleAppleWatch,
      Page.sleepWearable,
      Page.resultOverview,
    ]
    return flow
  },
  [WhichWearableBreakpointOption.ouraRing]: flow => {
    flow[allQuizSectionsAb351.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.equallyEffectiveInfo,
      Page.nightSleep,
      Page.sleepWearable,
      Page.resultOverview,
    ]
    return flow
  },
  [WhichWearableBreakpointOption.skip]: commonTransformFunctionWhichWearable,
}

const commonTransformFunctionSleep: ChoiceFunction<Page, QuizDataInput> = (flow, data) => {
  const appleWatch = data?.whichWearable?.includes(WhichWearableBreakpointOption.appleWatch)
  const ouraUser = data?.whichWearable?.includes(WhichWearableBreakpointOption.ouraRing)
  const sleepsThroughTheNight =
    data?.nightSleep === NightSleepBreakpointOption.sleepsThroughTheNight
  const isAppleWatchEligible = data?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes
  const isWillingToPurchaseAW =
    data?.purchaseNewWatch === PurchaseNewWatchOption.yes ||
    data?.consideringPurchase === ConsideringPurchaseOption.yesAppleWatch

  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    ...(appleWatch
      ? [Page.eligibleAppleWatch, Page.equallyEffectiveInfo]
      : [Page.equallyEffectiveInfo]),
    Page.nightSleep,
    ...(sleepsThroughTheNight
      ? [Page.sleepWearable]
      : [Page.troubleGettingBackToSleep, Page.sleepWearable]),
    ...((isAppleWatchEligible || isWillingToPurchaseAW) && !ouraUser
      ? [Page.wearYourWatchToBed, Page.sleepConsistency]
      : [Page.sleepConsistency]),
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}

export const nightSleep: BreakpointAction<NightSleepBreakpointOption, Page, QuizDataInput> = {
  [NightSleepBreakpointOption.sleepsThroughTheNight]: commonTransformFunctionSleep,
  [NightSleepBreakpointOption.sometimesWakesUp]: commonTransformFunctionSleep,
  [NightSleepBreakpointOption.oftenWakesUp]: commonTransformFunctionSleep,
}

const firstPathTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, data) => {
  const isAppleWatchEligible = data?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes
  const isWillingToPurchaseAW =
    data?.purchaseNewWatch === PurchaseNewWatchOption.yes ||
    data?.consideringPurchase === ConsideringPurchaseOption.yesAppleWatch
  const sleepsThroughTheNight =
    data?.nightSleep === NightSleepBreakpointOption.sleepsThroughTheNight
  const appleWatch = data?.whichWearable?.includes(WhichWearableBreakpointOption.appleWatch)
  const ouraUser = data?.whichWearable?.includes(WhichWearableBreakpointOption.ouraRing)

  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    ...(appleWatch
      ? [Page.eligibleAppleWatch, Page.equallyEffectiveInfo]
      : [Page.equallyEffectiveInfo]),
    Page.nightSleep,
    ...(sleepsThroughTheNight
      ? [Page.sleepWearable]
      : [Page.troubleGettingBackToSleep, Page.sleepWearable]),
    // the !ouraUser check is if they navigate back and change hardware selection
    // if Oura is added we need to make sure wearYourWatchToBed is not added
    ...((isAppleWatchEligible || isWillingToPurchaseAW) && !ouraUser
      ? [Page.wearYourWatchToBed, Page.sleepConsistency]
      : [Page.sleepConsistency]),
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}

const secondPathTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, data) => {
  const sleepsThroughTheNight =
    data?.nightSleep === NightSleepBreakpointOption.sleepsThroughTheNight
  const appleWatch = data?.whichWearable?.includes(WhichWearableBreakpointOption.appleWatch)

  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    ...(appleWatch
      ? [Page.eligibleAppleWatch, Page.equallyEffectiveInfo]
      : [Page.equallyEffectiveInfo]),
    Page.nightSleep,
    ...(sleepsThroughTheNight
      ? [Page.sleepWearable]
      : [Page.troubleGettingBackToSleep, Page.sleepWearable]),
    Page.sleepConsistency,
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}

export const sleepWearable: BreakpointAction<SleepWithTrackerOption, Page, QuizDataInput> = {
  [SleepWithTrackerOption.yesWithRing]: firstPathTransformFunction,
  [SleepWithTrackerOption.yesWithWatch]: secondPathTransformFunction,
  [SleepWithTrackerOption.yesWithBoth]: secondPathTransformFunction,
  [SleepWithTrackerOption.neither]: firstPathTransformFunction,
}

const commonTransformFunctionStress: ChoiceFunction<Page, QuizDataInput> = (flow, data) => {
  const appleWatch = data?.whichWearable?.includes(WhichWearableBreakpointOption.appleWatch)
  const isAppleWatchEligible = data?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes
  const isWillingToPurchaseAW =
    data?.purchaseNewWatch === PurchaseNewWatchOption.yes ||
    data?.consideringPurchase === ConsideringPurchaseOption.yesAppleWatch
  const sleepsThroughTheNight =
    data?.nightSleep === NightSleepBreakpointOption.sleepsThroughTheNight
  const ouraUser = data?.whichWearable?.includes(WhichWearableBreakpointOption.ouraRing)
  const wearNeitherOrRingToBed =
    data?.sleepWithTracker === SleepWithTrackerOption.neither ||
    data?.sleepWithTracker === SleepWithTrackerOption.yesWithRing

  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    ...(appleWatch
      ? [Page.eligibleAppleWatch, Page.equallyEffectiveInfo]
      : [Page.equallyEffectiveInfo]),
    Page.nightSleep,
    ...(sleepsThroughTheNight
      ? [Page.sleepWearable]
      : [Page.troubleGettingBackToSleep, Page.sleepWearable]),
    // the !ouraUser check is if they navigate back and change hardware selection
    // if Oura is added we need to make sure wearYourWatchToBed is not added
    ...((isAppleWatchEligible || isWillingToPurchaseAW) && wearNeitherOrRingToBed && !ouraUser
      ? [Page.wearYourWatchToBed, Page.sleepConsistency]
      : [Page.sleepConsistency]),
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}

const secondTransformFunction: ChoiceFunction<Page, QuizDataInput> = (flow, data) => {
  const appleWatch = data?.whichWearable?.includes(WhichWearableBreakpointOption.appleWatch)
  const isAppleWatchEligible = data?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes
  const isWillingToPurchaseAW =
    data?.purchaseNewWatch === PurchaseNewWatchOption.yes ||
    data?.consideringPurchase === ConsideringPurchaseOption.yesAppleWatch
  const sleepsThroughTheNight =
    data?.nightSleep === NightSleepBreakpointOption.sleepsThroughTheNight
  const ouraUser = data?.whichWearable?.includes(WhichWearableBreakpointOption.ouraRing)
  const wearNeitherOrRingToBed =
    data?.sleepWithTracker === SleepWithTrackerOption.neither ||
    data?.sleepWithTracker === SleepWithTrackerOption.yesWithRing

  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.equallyEffectiveInfo,
    ...(appleWatch
      ? [Page.eligibleAppleWatch, Page.equallyEffectiveInfo]
      : [Page.equallyEffectiveInfo]),
    Page.nightSleep,
    ...(sleepsThroughTheNight
      ? [Page.sleepWearable]
      : [Page.troubleGettingBackToSleep, Page.sleepWearable]),
    // the !ouraUser check is if they navigate back and change hardware selection
    // if Oura is added we need to make sure wearYourWatchToBed is not added
    ...((isAppleWatchEligible || isWillingToPurchaseAW) && wearNeitherOrRingToBed && !ouraUser
      ? [Page.wearYourWatchToBed, Page.sleepConsistency]
      : [Page.sleepConsistency]),
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}
export const feelingStressed: BreakpointAction<
  FeelingStressedBreakpointOption,
  Page,
  QuizDataInput
> = {
  [FeelingStressedBreakpointOption.always]: commonTransformFunctionStress,
  [FeelingStressedBreakpointOption.timeToTime]: commonTransformFunctionStress,
  [FeelingStressedBreakpointOption.noStress]: secondTransformFunction,
}

const commonTransformFunctionEligibleAppleWatch: ChoiceFunction<Page, QuizDataInput> = flow => {
  flow[allQuizSectionsAb351.routine] = [
    Page.naturalMethod,
    Page.yourMeasuringDevice,
    Page.whichWearable,
    Page.eligibleAppleWatch,
    Page.equallyEffectiveInfo,
    Page.nightSleep,
    Page.troubleGettingBackToSleep,
    Page.sleepWearable,
    Page.wearYourWatchToBed,
    Page.sleepConsistency,
    Page.spendingDaysInfo,
    Page.healthGoals,
    Page.feelingStressed,
    Page.biggestStressor,
    Page.favoriteWayToUnwind,
    Page.deviceTrait,
    Page.resultOverview,
  ]
  return flow
}

export const eligibleAppleWatch: BreakpointAction<
  EligibleAppleWatchBreakpointOption,
  Page,
  QuizDataInput
> = {
  [EligibleAppleWatchBreakpointOption.yes]: commonTransformFunctionEligibleAppleWatch,
  [EligibleAppleWatchBreakpointOption.no]: flow => {
    flow[allQuizSectionsAb351.routine] = [
      Page.naturalMethod,
      Page.yourMeasuringDevice,
      Page.whichWearable,
      Page.eligibleAppleWatch,
      Page.purchaseNewWatch,
      Page.equallyEffectiveInfo,
      Page.nightSleep,
      Page.troubleGettingBackToSleep,
      Page.sleepWearable,
      Page.wearYourWatchToBed,
      Page.sleepConsistency,
      Page.spendingDaysInfo,
      Page.healthGoals,
      Page.feelingStressed,
      Page.biggestStressor,
      Page.favoriteWayToUnwind,
      Page.deviceTrait,
      Page.resultOverview,
    ]
    return flow
  },
}

export const ab351Routes: Routes = [
  {
    path: Page.needsIntro,
    loadComponent: () =>
      import('./pages/needs-intro/needs-intro.page.component').then(m => m.NeedsIntroPage),
  },
  {
    path: Page.yourBody,
    loadComponent: () =>
      import('./pages/your-body/your-body.page.component').then(m => m.YourBodyPage),
  },
  {
    path: Page.understandingCycle,
    loadComponent: () =>
      import('./pages/understanding-cycle/understanding-cycle.page.component').then(
        m => m.UnderstandingCyclePage,
      ),
  },
  {
    path: Page.switchToNc,
    loadComponent: () =>
      import('./pages/switch-to-nc/switch-to-nc.page.component').then(m => m.SwitchToNCPage),
  },
  {
    path: Page.hesitations,
    loadComponent: () =>
      import('./pages/hesitations/hesitations.page.component').then(m => m.HesitationsPage),
  },
  {
    path: Page.ncInfo,
    loadComponent: () => import('./pages/nc-info/nc-info.page.component').then(m => m.NcInfoPage),
  },
  {
    path: Page.researchInfo,
    loadComponent: () =>
      import('./pages/research-info/research-info.page.component').then(m => m.ResearchInfoPage),
  },
  {
    path: Page.statementResearch,
    loadComponent: () =>
      import('./pages/statement-research/statement-research.page.component').then(
        m => m.StatementResearchPage,
      ),
  },
]
